import {
  AgChartsEnterpriseModule,
  LicenseManager as ChartsLicenseManager
} from "ag-charts-enterprise";
import {
  AllEnterpriseModule,
  ClientSideRowModelModule,
  enableDevValidations,
  LicenseManager,
  ModuleRegistry,
  SparklinesModule
} from "ag-grid-enterprise";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { isMswActive } from "./mockState";
import { reloadOnStaleChunk } from "./reloadOnStaleChunk";

/**
 * This allows us to enable mocking for demos or troubleshooting by setting
 * localStorage.msw = 'on' in the console.
 *
 * Default: OFF (the page talks to the real API). Set localStorage.msw = 'on'
 * to turn mocks on.
 *
 * How to force:
 *   - in console: localStorage.msw = 'on' (reload required)
 *   - to reset: localStorage.removeItem('msw')
 *
 * The same predicate (``isMswActive``) drives the ``MockBadge`` guard in
 * ``App.tsx``, so the indicator appears under exactly the conditions the
 * worker actually starts.
 */

async function enableMocking() {
  if (!isMswActive()) {
    return;
  }

  const { worker } = await import("./mocks/browser");
  await worker.start({
    onUnhandledRequest: import.meta.env.DEV ? "bypass" : "warn",
    serviceWorker: { url: `${import.meta.env.BASE_URL}mockServiceWorker.js` }
  });
}

/*
AG Grid setup
*/
// Grid config mistakes are silent unless validations are switched on.
if (import.meta.env.DEV) {
  enableDevValidations();
}

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  AllEnterpriseModule.with(AgChartsEnterpriseModule),
  SparklinesModule.with(AgChartsEnterpriseModule)
]);

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_KEY || "");
// The grid key covers integrated charts only; standalone <AgCharts> needs its own.
ChartsLicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_KEY || "");

reloadOnStaleChunk();

enableMocking().finally(() => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
});
